import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import styles from "./listWithUsForm.module.scss";
import {
  ACCOMMODATION_TYPES,
  EVENTS,
  ICONS,
  LIST_WITH_US_GOOGLE_SHEET_URL,
  NAME_PREFIX,
  DEFAULT_NAME_PREFIX,
} from "../../constants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import axios from "axios";
import urlJoin from "helpers/urljoin";
import config from "@Config/index";
import Image from "@Components/Image";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useWhitelabel from "hooks/useWhitelabel";
import useIsDesktop from "hooks/useIsDesktop";
import { logger } from "helpers/logger";
import classes from "@Components/Input/Input.module.scss";
import FormField from "@Pages/BookForm/components/FormField";
import Button from "@Components/Button";
import LoaderIcon from "@Icons/LoaderIcon";
import PartnerHeader from "../PartnerHeader/PartnerHeader";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import { useDispatch } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const ListWithUsForm = ({ closeForm }: any) => {
  const dispatch = useDispatch();
  const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);

  const { isWhitelabel, partnerName } = useWhitelabel();

  const isDesktop = useIsDesktop();

  const [showOptions, setShowOptions] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslationHook();

  const openDropdown = (label: string) => setShowOptions(label);

  const handleClear = (label: string) => {
    setValue(label as any, "");
    setShowOptions("");
  };

  const {
    getValues,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
    clearErrors,
  } = useForm({
    defaultValues: {
      honorifics: DEFAULT_NAME_PREFIX,
      name: "",
      email: "",
      countryCode: "",
      phone: "",
      country: "",
      accommodationType: "",
      websiteLink: "",
      propertyManagementSystem: "",
      additionalInfo: "",
      university: isWhitelabel ? partnerName : "",
    },
  });

  const countryCodeLabel = t("list.form.field.code.label");
  const countryLabel = t("list.form.field.country.label");

  const steps = {
    fields: [
      {
        name: "honorifics",
        placeholder: t("list.form.field.select.placeholder"),
        type: "dropdown",
        showPlaceholderAndLabel: true,
        showOptions,
        required: true,
        value: getValues("honorifics"),
        onFocus: () => openDropdown("honorifics"),
        handleClear: () => handleClear("honorifics"),
        handleOutsideClick: () => setShowOptions(""),
        span: 3,
        searchable: false,
        clearable: false,
        options: NAME_PREFIX(t),
        rules: {
          required: "This feild is required",
        },
        key: "honorifics",
      },
      {
        name: "name",
        label: t("list.form.field.name.label"),
        type: "text",
        placeholder: t("list.form.field.name.placeholder"),
        required: true,
        value: getValues("name"),
        span: 9,
        rules: {
          required: "This feild is required",
          minLength: {
            value: 2,
            message: "must contain atleast 2 characters",
          },
        },
        autoCapitalize: "on",
        key: "full_name",
      },
      {
        name: "email",
        label: t("list.form.field.email.label"),
        type: "text",
        inputType: "email",
        value: getValues("email"),
        placeholder: "name@example.com",
        required: true,
        span: 12,
        rules: {
          required: "This feild is required",
          pattern: {
            value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            message: "invalid email address",
          },
        },
        key: "email",
      },

      {
        name: "countryCode",
        label: countryCodeLabel,
        type: "countryCode",
        showOptions,
        value: getValues("countryCode"),
        required: true,
        handleOptionClick: (val: any) => {
          clearErrors("countryCode");
          setTimeout(() => setValue("countryCode", val), 0);
        },
        onFocus: () => openDropdown(countryCodeLabel),
        handleOutsideClick: () => setShowOptions(""),
        handleClear: () => handleClear("countryCode"),
        placeholder: t("list.form.field.code.placeholder"),
        autoComplete: true,
        span: !isDesktop ? 4 : 3,
        rules: {
          required: "This feild is required",
        },
        key: "countryCode",
      },
      {
        name: "phone",
        label: t("list.form.field.phone.label"),
        type: "number",
        required: true,
        value: getValues("phone"),
        placeholder: t("list.form.field.phone.placeholder"),
        span: !isDesktop ? 8 : 9,
        autoComplete: false,
        rules: {
          required: "This feild is required",
        },
        key: "phone_number",
      },
      ...(isWhitelabel
        ? [
            {
              name: "university",
              label: t("list.form.field.university.label"),
              placeholder: t("list.form.field.university.label"),
              type: "text",
              required: false,
              value: getValues("university"),
              disabled: true,
              span: !isDesktop ? 12 : 12,
              key: "university",
            },
          ]
        : []),
      {
        name: "country",
        label: countryLabel,
        placeholder: t("list.form.field.country.placeholder"),
        type: "searchableDropdown",
        required: true,
        showOptions,
        value: getValues("country"),
        handleOptionClick: (val: any) => {
          clearErrors("country");
          setValue("country", val);
        },
        onFocus: () => openDropdown(countryLabel),
        handleOutsideClick: () => setShowOptions(""),
        handleClear: () => handleClear("country"),
        clearable: true,
        autoComplete: false,
        options: countryList,
        span: 12,
        rules: {
          required: "This feild is required",
        },
        key: "country",
      },
      {
        name: "accommodationType",
        label: t("list.form.field.accommodationtype.label"),
        type: "dropdown",
        placeholder: t("list.form.field.select.placeholder"),
        showOptions,
        required: true,
        value: getValues("accommodationType"),
        onFocus: () => openDropdown("accommodationType"),
        handleClear: () => handleClear("accommodationType"),
        handleOutsideClick: () => setShowOptions(""),
        span: 12,
        searchable: false,
        clearable: false,
        options: ACCOMMODATION_TYPES(t),
        rules: {
          required: "This feild is required",
        },
        key: "accommodationType",
      },
      {
        name: "websiteLink",
        label: t("list.form.field.websitelink.label"),
        type: "text",
        placeholder: t("list.form.field.websitelink.label"),
        required: true,
        value: getValues("websiteLink"),
        span: 12,
        rules: {
          required: "This feild is required",
        },
        key: "websiteLink",
        showPlaceholderAndLabel: true,
      },
      {
        name: "propertyManagementSystem",
        label: t("list.form.field.propertymanagementsystem.label"),
        type: "text",
        placeholder: t("list.form.field.propertymanagementsystem.placeholder"),
        required: false,
        value: getValues("propertyManagementSystem"),
        span: 12,
        key: "propertyManagementSystem",
        showPlaceholderAndLabel: true,
      },
      {
        name: "additionalInfo",
        label: t("list.form.field.additionalinfo.label"),
        type: "text",
        placeholder: t("list.form.field.additionalinfo.placeholder"),
        required: false,
        value: getValues("additionalInfo"),
        span: 12,
        key: "additionalInfo",
        showPlaceholderAndLabel: true,
      },
    ],
  };

  useEffect(() => {
    gtagEventEmitter({
      action: EVENTS.onLandingFormPage,
      category: EVENTS.category,
    });
    fetchCountryList();
  }, []);

  const getCountryList = async (options: { url_path?: string; params?: any } = {}) => {
    const urlPath = options.url_path || "api/v0/regions";
    const url = urlJoin(config.API_URL, urlPath);
    const params = options.params || { region_types: "country", only: "name" };

    try {
      const response = await axios.get(url, { params });
      return response.data.data.result.reduce((acc: any, item: any) => {
        acc[item.name] = item.name;
        return acc;
      }, {});
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const fetchCountryList = async () => {
    try {
      const updatedCountryList = await getCountryList();
      setCountryList(updatedCountryList);
    } catch (error) {
      console.error(error);
    }
  };

  const formatDate = () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const transformFormData = (formObj: any) => {
    const form = new FormData();
    const {
      honorifics,
      name,
      email,
      countryCode,
      phone,
      country,
      accommodationType,
      websiteLink,
      propertyManagementSystem,
      additionalInfo,
    } = formObj;

    form.append("Mr", honorifics);
    form.append("Full name", name);
    form.append("Country code", countryCode);
    form.append("Phone number", phone);
    form.append("Email", email);
    form.append("Country", country);
    form.append("Accomodation type", accommodationType);
    form.append("Website Link", websiteLink);
    form.append("Mention Your Property Management System", propertyManagementSystem);
    form.append("Additional Information", additionalInfo);
    form.append("Submission Date", formatDate());
    return form;
  };

  const saveToGSheet = async (form: any) => {
    const scriptURL = LIST_WITH_US_GOOGLE_SHEET_URL;
    try {
      const response = await axios.post(scriptURL, form);
      toastNotificationDispatcher.openToast({
        title: "Success",
        message: "Successfully saved, We will connect with you soon",
      });
      setIsLoading(false);
      closeForm();
      return response;
    } catch (error: any) {
      setIsLoading(false);
      console.error("Error!", error?.message);
      toastNotificationDispatcher.openToast({
        title: "Error",
        message: "Something went wrong! Please try again",
        type: "error",
      });
      throw error;
    }
  };

  const listWithUs = async (payload: any) => {
    const url = urlJoin(config.API_URL, "/api/v0/utils/list_with_us");
    return axios.post(url, payload, { withCredentials: true });
  };

  const handleFormSubmit = async (formData: any) => {
    gtagEventEmitter({ action: EVENTS.formSubmit, category: EVENTS.category });
    setIsLoading(true);
    const form = transformFormData(formData);
    return Promise.allSettled([saveToGSheet(form), listWithUs(formData)]);
  };

  const onError = () => {
    try {
      setTimeout(() => {
        const item = document.getElementsByClassName(classes.error);
        if (item.length > 0) item[0].scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (error) {
      logger.error(error);
    }
  };

  const FormInputs = steps.fields.map((step: any) => (
    <div style={{ gridColumn: `span ${step.span}` }} key={step.label + step.name}>
      <FormField
        step={step}
        control={control}
        error={errors[step.name as keyof typeof errors]}
        value={getValues(step.name)}
        setValue={setValue}
        dataTestId={`bookform-${step.label}`}
      />
    </div>
  ));

  return (
    <div className={styles.modalContainer}>
      <div className={styles.headerContainer}>
        <PartnerHeader handleCTAClick={() => setShowOptions("")} isModalHeader />
      </div>
      <div className={formatClassNames(styles.content, styles.flex)}>
        {isDesktop && (
          <div className={styles.imageContainer}>
            <Image
              src={ICONS.listWithUsForm}
              alt="List with us"
              height={823}
              width={723}
              className={styles.formImage}
            />
          </div>
        )}
        <div className={formatClassNames(styles.formParentContainer, styles.flex, styles.column)}>
          <h1 className={styles.title}>{t("list.form.title")}</h1>
          <div className={formatClassNames(styles.formContainer, styles.flex, styles.column)}>
            <form onSubmit={handleSubmit(async (data) => handleFormSubmit(data), onError)}>
              <div className={styles.personalFormContainer}>{FormInputs}</div>
              <Button
                type="submit"
                className={formatClassNames(styles.button, isWhitelabel && styles.whitelabelButton)}
                size="lg"
                disable={isLoading}
                variant="none"
                RightIcon={() => (isLoading ? <LoaderIcon fill="#fff" size={20} /> : <></>)}
              >
                {isLoading ? t("list.form.submitting") : t("list.form.submit")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(ListWithUsForm, {
  componentName: "ListWithUsForm",
  showForChina: true,
});
